import MerchantLendingApi from '@/api/merchant/lending'
import {
  BUSINESS_LENDING_BANNER_STATUS,
  MERCHANT_LOAN_APPLICATION_STATUS,
  MERCHANT_LOAN_APPLICATION_STEP,
} from '@/constants/business-lending'

const fetchCurrentApplication = async (userId) => {
  const { data } = await MerchantLendingApi.fetchCurrentApplication({
    userId,
  })

  return data?.application ?? null
}

const fetchActiveSoftOffer = async (userId) => {
  const { data } = await MerchantLendingApi.fetchActiveSoftOffer({ userId })

  return data?.offer ?? null
}

const getBannerStatusFromApplication = (application) => {
  if (!application) {
    return ''
  }

  if (
    application.applicationStep === MERCHANT_LOAN_APPLICATION_STEP.ACTIVE &&
    application.applicationStatus === MERCHANT_LOAN_APPLICATION_STATUS.ACTIVE
  ) {
    return BUSINESS_LENDING_BANNER_STATUS.READY
  }

  if (
    application.applicationStatus ===
      MERCHANT_LOAN_APPLICATION_STATUS.CANCELLED ||
    application.applicationStatus === MERCHANT_LOAN_APPLICATION_STATUS.REJECTED
  ) {
    return ''
  }

  return BUSINESS_LENDING_BANNER_STATUS.IN_PROGRESS
}

const state = {
  application: null,
  softOffer: null,
  merchantId: '',
  bannerStatus: '',
  fetched: false,
  fetching: false,
  error: null,
}

const getters = {
  businessLendingApplication: (state) => state.application,
  businessLendingApplicationId: (state) =>
    state.application?.applicationId || '',
  businessLendingSoftOffer: (state) => state.softOffer,
  businessLendingMerchantId: (state) => state.merchantId,
  businessLendingBannerStatus: (state) => state.bannerStatus,
  isFetchingBusinessLendingBannerStatus: (state) => state.fetching,
  hasFetchedBusinessLendingBannerStatus: (state) => state.fetched,
  hasBusinessLendingBannerStatusError: (state) => state.error !== null,
}

const mutations = {
  SET_STATUS(state, { key, value }) {
    state[key] = value
  },
  SET_BANNER_STATUS(state, bannerStatus) {
    state.bannerStatus = bannerStatus
  },
  SET_APPLICATION(state, application) {
    state.application = application
  },
  SET_SOFT_OFFER(state, softOffer) {
    state.softOffer = softOffer
  },
  SET_MERCHANT_ID(state, merchantId) {
    state.merchantId = merchantId
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  RESET_BUSINESS_LENDING(state) {
    state.application = null
    state.softOffer = null
    state.merchantId = ''
    state.bannerStatus = ''
    state.fetched = false
    state.fetching = false
    state.error = null
  },
}

const actions = {
  async fetchBusinessLendingCurrentApplication({
    commit,
    dispatch,
    rootGetters,
    state,
  }) {
    const merchantId = rootGetters['userProfile/selectedMerchant'] || ''

    if (state.fetching && state.merchantId === merchantId) {
      return
    }

    try {
      commit('SET_MERCHANT_ID', merchantId)
      commit('SET_STATUS', { key: 'fetching', value: true })
      commit('SET_STATUS', { key: 'fetched', value: false })
      commit('SET_APPLICATION', null)
      commit('SET_SOFT_OFFER', null)
      commit('SET_BANNER_STATUS', '')
      commit('SET_ERROR', null)

      let userId = rootGetters['userProfile/profile']?.userId || ''

      if (!userId) {
        const userProfile = await dispatch(
          'userProfile/getMerchantUserProfile',
          null,
          { root: true }
        )
        userId = userProfile?.userId || ''
      }

      const application = await fetchCurrentApplication(userId)

      if (state.merchantId !== merchantId) {
        return
      }

      commit('SET_APPLICATION', application)

      if (application) {
        commit('SET_BANNER_STATUS', getBannerStatusFromApplication(application))
        commit('SET_STATUS', { key: 'fetched', value: true })
        return
      }

      const softOffer = await fetchActiveSoftOffer(userId)

      if (state.merchantId !== merchantId) {
        return
      }

      commit('SET_SOFT_OFFER', softOffer)
      commit(
        'SET_BANNER_STATUS',
        softOffer ? BUSINESS_LENDING_BANNER_STATUS.INTRO : ''
      )
      commit('SET_STATUS', { key: 'fetched', value: true })
    } catch (error) {
      if (state.merchantId !== merchantId) {
        return
      }

      commit('SET_APPLICATION', null)
      commit('SET_SOFT_OFFER', null)
      commit('SET_BANNER_STATUS', '')
      commit('SET_ERROR', error)
      commit('SET_STATUS', { key: 'fetched', value: true })
    } finally {
      if (state.merchantId === merchantId) {
        commit('SET_STATUS', { key: 'fetching', value: false })
      }
    }
  },
  resetBusinessLending({ commit }) {
    commit('RESET_BUSINESS_LENDING')
  },
}

export default { namespaced: true, state, getters, mutations, actions }
